import {
    Add,
    AssignmentTurnedIn,
    Delete,
    Edit,
    OpenInNew,
    SubdirectoryArrowRight,
    Warning,
} from "@mui/icons-material";
import {
    Box,
    Grid,
    IconButton,
    Paper,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from "@mui/material";
import AcxButton from "components/UI/AcxButton";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import ClassifierGroup from "models/ClassifierGroup";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import ClassifierService from "services/ClassifierService";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import {
    buildChildrenMap,
    buildGroupById,
    getAncestorPath,
    getChildren,
} from "./classifierGroupTree";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import ManageGroupClassifiersDialog from "./ManageGroupClassifiersDialog";
import { ClassifierInGroup } from "services/ClassifierService";

type GroupSelectorProps = Partial<
    Omit<
        React.ComponentPropsWithoutRef<typeof AcxSelectSingle>,
        "options" | "dropdownMenuEndChildren" | "onChange" | "defaultValue"
    >
> & {
    /**
     * Maximum number of cascading selection levels. `1` (default) is a single
     * root-group dropdown; `2` allows a parent group plus an optional sub group.
     * Exactly this many dropdowns are always rendered.
     */
    maxDepth?: number;
    /** The currently selected (deepest) group. */
    defaultValue?: ClassifierGroup;
    /** Emits the deepest selected group, or undefined when cleared. */
    onChange?: (group?: ClassifierGroup) => void;
};

/**
 * Group selector with inline management. Renders up to `maxDepth` cascading
 * dropdowns (root group, then sub groups). The selection is exposed as a single
 * ClassifierGroup — the deepest chosen group — so callers persist one
 * `classifierGroupId`.
 */
export function GroupSelector(props: GroupSelectorProps) {
    const {
        maxDepth = 1,
        id = "group",
        inputLabel,
        labelIcon,
        defaultValue,
        onChange,
        ...rest
    } = props;

    const [groups, setGroups] = useState<ClassifierGroup[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [createContext, setCreateContext] = useState<{
        level: number;
        parent?: ClassifierGroup;
    } | null>(null);
    const [isManageOpen, setIsManageOpen] = useState(false);

    const isLargeUp = useMediaQuery(theme.breakpoints.up("lg"));

    const fetchGroups = useCallback(async (signal?: AbortSignal) => {
        try {
            setIsLoading(true);
            const service = new ClassifierService();
            const result = await service.getClassifierGroups(signal);
            setGroups(result);
            return result;
        } catch {
            return undefined;
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        const abortController = new AbortController();
        void fetchGroups(abortController.signal);
        return () => abortController.abort();
    }, [fetchGroups]);

    const { byId, childMap } = useMemo(
        () => ({
            byId: buildGroupById(groups),
            childMap: buildChildrenMap(groups),
        }),
        [groups],
    );

    // The selection is fully derived from the caller's value (the stored leaf
    // group) plus the live tree, so the component stays controlled.
    const selectedLeaf = defaultValue as ClassifierGroup | undefined;
    const selectedPath = useMemo(
        () => getAncestorPath(byId, selectedLeaf?.id).slice(0, maxDepth),
        [byId, selectedLeaf?.id, maxDepth],
    );

    const emitPath = useCallback(
        (path: string[]) => {
            const deepestId = path[path.length - 1];
            onChange?.(deepestId ? byId.get(deepestId) : undefined);
        },
        [byId, onChange],
    );

    const handleLevelChange = (level: number, group?: ClassifierGroup) => {
        if (!group?.id) {
            emitPath(selectedPath.slice(0, level));
            return;
        }
        emitPath([...selectedPath.slice(0, level), group.id]);
    };

    const onGroupCreated = async (created: ClassifierGroup) => {
        await fetchGroups();
        // Auto-select the freshly created group at its level.
        onChange?.(created);
    };

    const onGroupDeleted = (deletedId: string) => {
        const idx = selectedPath.indexOf(deletedId);
        if (idx !== -1) {
            emitPath(selectedPath.slice(0, idx));
        }
        void fetchGroups();
    };

    const levels = Array.from({ length: Math.max(1, maxDepth) }, (_, level) => {
        const parentId = level === 0 ? null : selectedPath[level - 1];
        const parentChosen = level === 0 || Boolean(selectedPath[level - 1]);
        const options = parentChosen
            ? getChildren(childMap, parentId ?? null)
            : [];
        const selectedId = selectedPath[level];
        return { level, parentId, parentChosen, options, selectedId };
    });

    return (
        <>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {levels.map((lvl) => {
                    const parent = lvl.parentId
                        ? byId.get(lvl.parentId)
                        : undefined;
                    const selectedOption = lvl.selectedId
                        ? lvl.options.find((g) => g.id === lvl.selectedId)
                        : undefined;
                    return (
                        <Box
                            key={`level-${lvl.level}-${lvl.parentId ?? "root"}`}
                            minWidth="220px"
                            flex={1}
                        >
                            <AcxSelectSingle
                                {...rest}
                                id={
                                    lvl.level === 0
                                        ? id
                                        : `${id}-sub-${lvl.level}`
                                }
                                valueField="id"
                                labelField="name"
                                fullWidth
                                inputLabel={
                                    lvl.level === 0 ? inputLabel : "Sub Group"
                                }
                                labelIcon={
                                    lvl.level === 0 ? labelIcon : undefined
                                }
                                enableNoSelection={!isLoading}
                                isLoading={isLoading}
                                isDisabled={!lvl.parentChosen}
                                options={lvl.options
                                    .slice()
                                    .sort((a, b) =>
                                        a.name.localeCompare(b.name),
                                    )}
                                defaultValue={selectedOption}
                                onChange={(group?: ClassifierGroup) =>
                                    handleLevelChange(lvl.level, group)
                                }
                                dropdownMenuEndChildren={
                                    <Grid
                                        container
                                        direction="column"
                                        rowGap={1 / 2}
                                        flexWrap="nowrap"
                                        paddingTop={1}
                                    >
                                        <Grid item>
                                            <AcxButton
                                                fullWidth
                                                disabled={isLoading}
                                                startIcon={
                                                    isLargeUp ? (
                                                        lvl.level === 0 ? (
                                                            <Add />
                                                        ) : (
                                                            <SubdirectoryArrowRight />
                                                        )
                                                    ) : undefined
                                                }
                                                onClick={() =>
                                                    setCreateContext({
                                                        level: lvl.level,
                                                        parent,
                                                    })
                                                }
                                            >
                                                {lvl.level === 0
                                                    ? "Create New Group"
                                                    : "Create Sub Group"}
                                            </AcxButton>
                                        </Grid>
                                        <Grid item>
                                            <AcxButton
                                                fullWidth
                                                disabled={isLoading}
                                                startIcon={
                                                    isLargeUp ? (
                                                        <OpenInNew />
                                                    ) : undefined
                                                }
                                                onClick={() =>
                                                    setIsManageOpen(true)
                                                }
                                            >
                                                Manage Groups
                                            </AcxButton>
                                        </Grid>
                                    </Grid>
                                }
                            />
                        </Box>
                    );
                })}
            </Stack>
            <ManageGroupDialog
                isOpen={createContext !== null}
                onOpenChange={(open) => {
                    if (!open) setCreateContext(null);
                }}
                isEditDialog={false}
                parentGroup={createContext?.parent}
                onSuccess={(group) => {
                    void onGroupCreated(group);
                }}
            />
            <ManageGroupsDialog
                isOpen={isManageOpen}
                onOpenChange={setIsManageOpen}
                groups={groups}
                childMap={childMap}
                byId={byId}
                onChanged={() => void fetchGroups()}
                onGroupDeleted={onGroupDeleted}
            />
        </>
    );
}

interface ManageGroupDialogProps {
    isOpen: boolean;
    onOpenChange(open: boolean): void;
    onSuccess?(group: ClassifierGroup): void;
    isEditDialog: boolean;
    defaultGroup?: ClassifierGroup;
    /** When creating a sub group, the parent it should be nested under. */
    parentGroup?: ClassifierGroup;
}

function ManageGroupDialog(props: ManageGroupDialogProps) {
    const messageStore = useStore(MessageStore);

    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [value, setValue] = useState(props.defaultGroup?.name ?? "");

    useEffect(() => {
        if (props.isOpen) setValue(props.defaultGroup?.name ?? "");
    }, [props.isOpen, props.defaultGroup]);

    const service = useMemo(() => new ClassifierService(), []);

    const create = useCallback(
        async (name: string, parentGroupId?: string) => {
            try {
                setIsSaveLoading(true);
                const group = await service.createClassifierGroup(
                    name,
                    parentGroupId,
                );
                messageStore.logMessage("Group created", "success");
                return group;
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(
                        "Failed to create group: " + error.message,
                    );
                }
            } finally {
                setIsSaveLoading(false);
            }
        },
        [service, messageStore],
    );

    const update = useCallback(
        async (id: string, name: string) => {
            try {
                setIsSaveLoading(true);
                const group = await service.updateClassifierGroup(id, name);
                messageStore.logMessage("Group updated", "success");
                return group;
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(
                        "Failed to update group: " + error.message,
                    );
                }
            } finally {
                setIsSaveLoading(false);
            }
        },
        [service, messageStore],
    );

    const title = props.isEditDialog
        ? "Edit Group"
        : props.parentGroup
        ? `Create Sub Group in "${props.parentGroup.name}"`
        : "Create New Group";

    return (
        <AcxDialog
            isOpen={props.isOpen}
            onClose={() => props.onOpenChange(false)}
            contentWidth="480px"
            title={title}
            dialogContentChildren={
                <AcxMainTextField
                    id="group-name"
                    labelText="Group Name"
                    type="input"
                    fullWidth
                    value={value}
                    placeholderText="Enter..."
                    onChange={(event) => setValue(event.target.value)}
                    error={false}
                />
            }
        >
            <AcxButton
                color="primary"
                loading={isSaveLoading}
                disabled={value === ""}
                onClick={async () => {
                    let group: ClassifierGroup | undefined;
                    if (props.isEditDialog && props.defaultGroup) {
                        group = await update(props.defaultGroup.id, value);
                    } else {
                        group = await create(value, props.parentGroup?.id);
                        setValue("");
                    }

                    props.onOpenChange(false);
                    if (group) props.onSuccess?.(group);
                }}
            >
                Save
            </AcxButton>
        </AcxDialog>
    );
}

interface ManageGroupsDialogProps {
    isOpen: boolean;
    onOpenChange(open: boolean): void;
    groups: ClassifierGroup[];
    childMap: Map<string, ClassifierGroup[]>;
    byId: Map<string, ClassifierGroup>;
    /** Called after any create/edit/delete so the caller can refetch. */
    onChanged(): void;
    /** Called with the id of a group that was deleted. */
    onGroupDeleted(deletedId: string): void;
}

function ManageGroupsDialog(props: ManageGroupsDialogProps) {
    const messageStore = useStore(MessageStore);
    const service = useMemo(() => new ClassifierService(), []);

    const [editTarget, setEditTarget] = useState<ClassifierGroup>();
    const [createParent, setCreateParent] = useState<ClassifierGroup>();
    const [manageClassifiersTarget, setManageClassifiersTarget] =
        useState<ClassifierGroup>();
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);

    const [deleteTarget, setDeleteTarget] = useState<ClassifierGroup>();
    const [deleteClassifiers, setDeleteClassifiers] =
        useState<ClassifierInGroup[]>();
    const [isDeleteLoading, setIsDeleteLoading] = useState(false);

    const [groupIdsInLibrary, setGroupIdsInLibrary] = useState<string[]>();

    useEffect(() => {
        if (!props.isOpen) return;
        const abortController = new AbortController();
        const fetchGroupIds = async () => {
            try {
                const groupIds =
                    await service.getGroupIdsWithClassifierInLibrary(
                        abortController.signal,
                    );
                setGroupIdsInLibrary(groupIds);
            } catch {}
        };
        fetchGroupIds();
        return () => abortController.abort();
    }, [service, props.isOpen]);

    // Load the classifiers that will be unassigned when a delete is requested.
    useEffect(() => {
        if (!deleteTarget) {
            setDeleteClassifiers(undefined);
            return;
        }
        const abortController = new AbortController();
        const fetchClassifiers = async () => {
            try {
                const result = await service.getClassifiersInGroup(
                    deleteTarget.id,
                    abortController.signal,
                );
                setDeleteClassifiers(result);
            } catch {
                setDeleteClassifiers([]);
            }
        };
        fetchClassifiers();
        return () => abortController.abort();
    }, [service, deleteTarget]);

    const deleteGroup = useCallback(
        async (id: string) => {
            try {
                setIsDeleteLoading(true);
                await service.deleteClassifierGroup(id);
                messageStore.logMessage("Group deleted", "success");
                return true;
            } catch (error) {
                if (error instanceof Error) {
                    messageStore.logError(
                        "Failed to delete group: " + error.message,
                    );
                }
                return false;
            } finally {
                setIsDeleteLoading(false);
            }
        },
        [service, messageStore],
    );

    const roots = getChildren(props.childMap, null);

    const renderNode = (group: ClassifierGroup, depth: number) => {
        const children = getChildren(props.childMap, group.id);
        const hasChildren = children.length > 0;
        const isInLibrary = groupIdsInLibrary?.includes(group.id);
        const deleteDisabled =
            hasChildren || isInLibrary || groupIdsInLibrary === undefined;
        const deleteTooltip = hasChildren
            ? "Delete this group's sub groups first"
            : isInLibrary
            ? "Groups with classifiers in the library cannot be deleted"
            : "Delete group";

        return (
            <React.Fragment key={group.id}>
                <Paper
                    style={{
                        borderRadius: "0.5rem",
                        backgroundColor: theme.palette.lightgray.main,
                        marginLeft: `${depth * 1.5}rem`,
                    }}
                >
                    <Grid
                        container
                        item
                        direction="row"
                        justifyContent="space-between"
                        padding={2}
                        flexWrap="nowrap"
                        alignItems="center"
                    >
                        <Grid item>
                            <Typography
                                style={{
                                    fontSize: "16px",
                                    color: theme.palette.blackFont.main,
                                }}
                            >
                                {group.name}
                            </Typography>
                        </Grid>
                        <Grid
                            container
                            item
                            direction="row"
                            gap={1}
                            width="fit-content"
                            alignItems="center"
                        >
                            <Tooltip title="Add sub group">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => {
                                        setCreateParent(group);
                                        setIsCreateOpen(true);
                                    }}
                                >
                                    <SubdirectoryArrowRight />
                                </IconButton>
                            </Tooltip>
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => {
                                    setEditTarget(group);
                                    setIsEditOpen(true);
                                }}
                            >
                                <Edit />
                            </IconButton>
                            <Tooltip title="Manage classifiers">
                                <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                        setManageClassifiersTarget(group)
                                    }
                                >
                                    <AssignmentTurnedIn />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={deleteTooltip}>
                                <span>
                                    <IconButton
                                        size="small"
                                        disabled={deleteDisabled}
                                        onClick={() => setDeleteTarget(group)}
                                    >
                                        <Delete />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        </Grid>
                    </Grid>
                </Paper>
                {children.map((child) => renderNode(child, depth + 1))}
            </React.Fragment>
        );
    };

    return (
        <>
            <AcxDialog
                isOpen={props.isOpen}
                onClose={() => props.onOpenChange(false)}
                title="Manage Groups"
                maxWidth="lg"
                contentWidth="728px"
                dialogContentChildren={
                    <Grid container direction="column" gap={1}>
                        {roots.map((group) => renderNode(group, 0))}
                        <AcxButton
                            startIcon={<Add />}
                            onClick={() => {
                                setCreateParent(undefined);
                                setIsCreateOpen(true);
                            }}
                        >
                            New Group
                        </AcxButton>
                    </Grid>
                }
            ></AcxDialog>

            <ManageGroupDialog
                isOpen={isCreateOpen}
                onOpenChange={setIsCreateOpen}
                isEditDialog={false}
                parentGroup={createParent}
                onSuccess={() => props.onChanged()}
            />
            {editTarget && (
                <ManageGroupDialog
                    isOpen={isEditOpen}
                    onOpenChange={setIsEditOpen}
                    isEditDialog={true}
                    defaultGroup={editTarget}
                    onSuccess={() => props.onChanged()}
                />
            )}
            {manageClassifiersTarget && (
                <ManageGroupClassifiersDialog
                    isOpen={Boolean(manageClassifiersTarget)}
                    group={manageClassifiersTarget}
                    groups={props.groups}
                    byId={props.byId}
                    mode="manage"
                    onClose={() => setManageClassifiersTarget(undefined)}
                    onApplied={() => props.onChanged()}
                />
            )}
            {deleteTarget && (
                deleteClassifiers && deleteClassifiers.length > 0 ? (
                    <ManageGroupClassifiersDialog
                        isOpen={Boolean(deleteTarget)}
                        group={deleteTarget}
                        groups={props.groups}
                        byId={props.byId}
                        mode="resolveDelete"
                        onClose={() => setDeleteTarget(undefined)}
                        onApplied={async (response) => {
                            if (response.failures.length > 0) return;

                            const remaining =
                                await service.getClassifiersInGroup(
                                    deleteTarget.id,
                                );
                            setDeleteClassifiers(remaining);
                            if (remaining.length > 0) return;

                            const id = deleteTarget.id;
                            const ok = await deleteGroup(id);
                            setDeleteTarget(undefined);
                            if (ok) {
                                props.onGroupDeleted(id);
                                props.onChanged();
                            }
                        }}
                    />
                ) : (
                    <AcxDialog
                        isOpen={Boolean(deleteTarget)}
                        onClose={() => setDeleteTarget(undefined)}
                        title={<Warning color="error" />}
                        subTitle={`Delete "${deleteTarget.name}"`}
                        text={
                            deleteClassifiers === undefined
                                ? "Loading assigned classifiers..."
                                : "Are you sure you want to delete this group? This action cannot be undone."
                        }
                    >
                        <AcxButton
                            color="primary"
                            loading={isDeleteLoading}
                            disabled={deleteClassifiers === undefined}
                            onClick={async () => {
                                const id = deleteTarget.id;
                                const ok = await deleteGroup(id);
                                setDeleteTarget(undefined);
                                if (ok) {
                                    props.onGroupDeleted(id);
                                    props.onChanged();
                                }
                            }}
                        >
                            Ok
                        </AcxButton>
                    </AcxDialog>
                )
            )}
        </>
    );
}
